export type UploadKind = "gib-customs" | "gib-export-sales" | "declaration" | "ledger" | "opening" | "inventory" | "bom" | "capacity" | "capacity-recipe" | "manufacturer" | "chamber";
export type UploadProgress = { current: number; total: number; unit?: string };
type QueuedUpload = {
  kind: UploadKind;
  items: unknown[];
  uploadItem: (item: unknown) => Promise<void>;
  success: string | (() => string);
  unit?: string;
};
type UploadState = {
  progress: Partial<Record<UploadKind, UploadProgress>>;
  message: string;
  error: string;
  completed: number;
};
type UploadQueue = {
  state: UploadState;
  items: QueuedUpload[];
  running: boolean;
  listeners: Set<(state: UploadState) => void>;
};

// Only the current browser session's uploads. Navigation detaches the view,
// not the queue; finished queues are released when their last view leaves.
const queues = new Map<number, UploadQueue>();
export function hasPendingUploads(caseId: number): boolean {
  return Object.keys(queues.get(caseId)?.state.progress || {}).length > 0;
}
function queueFor(caseId: number): UploadQueue {
  let queue = queues.get(caseId);
  if (!queue) {
    queue = { state: { progress: {}, message: "", error: "", completed: 0 }, items: [], running: false, listeners: new Set() };
    queues.set(caseId, queue);
  }
  return queue;
}
function publish(queue: UploadQueue, state: Partial<UploadState>) {
  queue.state = { ...queue.state, ...state };
  queue.listeners.forEach((listener) => listener(queue.state));
}
export function subscribeUploads(caseId: number, listener: (state: UploadState) => void) {
  const queue = queueFor(caseId);
  queue.listeners.add(listener);
  listener(queue.state);
  return () => {
    queue.listeners.delete(listener);
    if (!queue.running && !queue.items.length && !queue.listeners.size) queues.delete(caseId);
  };
}
async function processQueue(caseId: number, queue: UploadQueue) {
  if (queue.running) return;
  queue.running = true;
  while (queue.items.length) {
    const upload = queue.items.shift()!;
    const errors: string[] = [];
    for (let index = 0; index < upload.items.length; index += 1) {
      publish(queue, { progress: { ...queue.state.progress, [upload.kind]: { current: index + 1, total: upload.items.length, unit: upload.unit } } });
      try {
        await upload.uploadItem(upload.items[index]);
      } catch (reason) {
        const item = upload.items[index];
        const name = item && typeof item === "object" && "name" in item ? `${item.name}: ` : "";
        errors.push(name + (reason instanceof Error ? reason.message : "Yükleme tamamlanamadı."));
      }
    }
    const progress = { ...queue.state.progress };
    delete progress[upload.kind];
    publish(queue, {
      progress,
      completed: queue.state.completed + 1,
      message: errors.length ? "" : typeof upload.success === "function" ? upload.success() : upload.success,
      error: [queue.state.error, ...errors].filter(Boolean).join("\n")
    });
  }
  queue.running = false;
  if (!queue.listeners.size) queues.delete(caseId);
}
export function enqueueUpload<T>(caseId: number, kind: UploadKind, items: T[], uploadItem: (item: T) => Promise<void>, success: QueuedUpload["success"], unit?: string) {
  const queue = queueFor(caseId);
  if (!items.length || queue.state.progress[kind]) return;
  queue.items.push({ kind, items, uploadItem: (item) => uploadItem(item as T), success, unit });
  publish(queue, { progress: { ...queue.state.progress, [kind]: { current: 0, total: items.length, unit } }, error: "", message: "" });
  void processQueue(caseId, queue);
}
